const API_BASE_URL: string = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
const DEV_MODE: boolean = import.meta.env.VITE_DEV_MODE === "true";

function withBase(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export type ProfilePayload = Partial<{
  displayName: string;
  cellNumber: string;
  coachReplyEmail: string | null;
  gpa: number | null;
  weightedGpa: number | null;
  satScore: number | null;
  actScore: number | null;
  academicSelectivity: "very_selective" | "selective" | "open";
  divisionsOpenTo: string[];
  preferredRegions: string[];
  costSensitivity: "need_aid" | "flexible";
  onboardingRole: "player" | "supporter";
  preferredStates: string[];
  maxAnnualNetPrice: number | null;
  schoolPreferencesNotes: string | null;
  locationPreferenceMode: "anywhere" | "home" | "nearby" | "regions" | "specific";
  juniorGolfHighlights: string[];
  preferredMajors: string[];
  swingCoachName: string;
  swingCoachContact: string;
  swingVideoUrl: string;
  swingSpeed: number | null;
  personalStatement: string | null;
  characterStories: string | null;
  otherSports: string[];
  activitiesAndLeadership: string[];
  athleticBackground: string | null;
  linkedJgsProfile: string;
  gradYear: number | null;
  state: string;
  gender: string;
  onboardingComplete: boolean;
  onboardingVersion: number;
  onboardingStepId: string | null;
  onboardingCompletedStepIds: string[];
  onboardingDraft: Record<string, unknown>;
  completedChecklistSteps: number[];
  showClubGolf: boolean;
  dismissedProfileFields?: string[];
}>;

export async function getMyProfile(): Promise<any> {
  if (DEV_MODE) {
    // In dev mode, defer to /users/me if available via api.ts; return null here
    // to keep behavior consistent with existing mock patterns.
    return null;
  }
  const resp = await fetch(withBase("/users/me"), {
    method: "GET",
    credentials: "include",
  });
  if (!resp.ok) throw new Error(`Failed to load profile (${resp.status})`);
  return await resp.json();
}

export async function updateMyProfile(payload: ProfilePayload): Promise<any> {
  if (DEV_MODE) {
    return { success: true, profile: payload };
  }
  const resp = await fetch(withBase("/users/me/info"), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const text = await resp.text();
  let data: any;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!resp.ok) {
    throw new Error(data?.error || data?.message || `Failed to update profile (${resp.status})`);
  }
  return data;
}

export async function updateProfileField<T extends keyof ProfilePayload>(
  key: T,
  value: ProfilePayload[T],
  linkedJgsProfile?: string
): Promise<any> {
  const payload: ProfilePayload = { [key]: value } as ProfilePayload;
  if (linkedJgsProfile) {
    payload.linkedJgsProfile = linkedJgsProfile;
  }
  return updateMyProfile(payload);
}

let majorsCache: string[] | null = null;

export async function fetchMajorsList(): Promise<string[]> {
  if (majorsCache) return majorsCache;
  const resp = await fetch(withBase("/colleges/majors/list"), {
    method: "GET",
    credentials: "include",
  });
  if (!resp.ok) throw new Error(`Failed to fetch majors (${resp.status})`);
  const data = await resp.json();
  majorsCache = data.majors || [];
  return majorsCache!;
}
